import { 
  TrendingUp, Users, Eye, Smartphone, BarChart3, 
  CheckCircle2, ArrowRight, Utensils, Zap, Clock, Shield, 
  CreditCard
} from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/SEO";

const WarumSpeiseGuruPage = () => {
  // Stats als Variablen (später aus API)
  const stats = {
    monthlyVisitors: "10.000+",
    restaurantsListed: "500+",
    menuViewsPerMonth: "50.000+",
    mobileUsage: "95%",
    lastUpdated: "Januar 2026"
  };

  const benefits = [
    {
      icon: <Eye className="h-8 w-8" />,
      title: "Mehr Sichtbarkeit",
      description: "SEO + Standort + Speisekarten-Preview",
      outcome: "Werde von mehr potenziellen Gästen gefunden und steigere deine Reichweite.",
      bullets: [
        "In Google & lokalen Suchen gefunden werden",
        "Standortbasierte Suche für Gäste in deiner Nähe",
        "Speisekarten-Preview erhöht Klickrate"
      ],
      color: "from-primary to-primary-light"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Mehr Gäste",
      description: "Direkter Kontakt + bessere Conversion",
      outcome: "Gewinne mehr Gäste durch professionelle Präsentation und direkte Kontaktmöglichkeiten.",
      bullets: [
        "Gäste sehen deine Speisekarte vor dem Besuch",
        "Direkte Kontaktmöglichkeiten (Anruf, Route, Bestellung)",
        "Weniger Absagen durch klare Erwartungen"
      ],
      color: "from-primary to-primary-light"
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: "Mehr Umsatz",
      description: "Höhere Bestellwerte + gezieltere Gäste",
      outcome: "Steigere deinen Durchschnittsbestellwert durch informierte Gäste.",
      bullets: [
        "Gäste bestellen mehr, wenn sie die Karte vorab sehen",
        "Gezieltere Gäste = weniger Absagen",
        "Bessere Auslastung durch Vorab-Information"
      ],
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <Smartphone className="h-8 w-8" />,
      title: "Mobile-optimiert",
      description: "Perfekt auf jedem Gerät",
      outcome: "Erreiche über 80% deiner potenziellen Gäste auf mobilen Geräten.",
      bullets: [
        "Über 80% der Suchenden nutzen mobile Geräte",
        "Scharfe, lesbare Speisekarten auf allen Bildschirmen",
        "Schnelle Ladezeiten für bessere User Experience"
      ],
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: "Daten & Insights",
      description: "Verstehe deine Gäste besser",
      outcome: "Nutze wertvolle Daten für bessere Entscheidungen und mehr Erfolg.",
      bullets: [
        "Sieh, wie oft deine Speisekarte angesehen wird",
        "Erkenne beliebte Gerichte und Trends",
        "Nutze Daten für bessere Entscheidungen"
      ],
      color: "from-primary-dark to-primary"
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Schnell & Einfach",
      description: "In wenigen Minuten online",
      outcome: "Starte in unter 3 Minuten – ohne komplizierte Technik oder lange Wartezeiten.",
      bullets: [
        "Registrierung dauert < 3 Minuten",
        "Speisekarte später hochladen möglich",
        "Keine komplizierte Technik nötig"
      ],
      color: "from-yellow-500 to-orange-500"
    }
  ];

  const featuresByCategory = {
    profil: {
      title: "Profil & Sichtbarkeit",
      items: [
        "Kostenlose Registrierung",
        "SEO-optimierte Restaurantseiten",
        "Automatische Standortoptimierung",
        "Direkter Kontakt zu interessierten Gästen"
      ]
    },
    speisekarten: {
      title: "Speisekarten & Verwaltung",
      items: [
        "Einfache Speisekarten-Verwaltung",
        "Hochauflösende Darstellung",
        "Mobile-optimierte Ansicht",
        "Eigene Updates jederzeit möglich"
      ]
    },
    kosten: {
      title: "Kosten & Transparenz",
      items: [
        "Keine versteckten Kosten",
        "Kostenloser Basiseintrag",
        "Transparente Preise",
        "Jederzeit kündbar"
      ]
    }
  };

  const process = [
    {
      step: "1",
      title: "Registrieren (kostenlos)",
      description: "Erstelle dein Restaurant-Profil in wenigen Minuten. Keine Kreditkarte nötig."
    },
    {
      step: "2",
      title: "Speisekarte hochladen",
      description: "Lade deine Speisekarte hoch oder reiche einen Link ein. Wir optimieren sie für die beste Darstellung."
    },
    {
      step: "3",
      title: "Profil prüfen & veröffentlichen",
      description: "Prüfe dein Profil, ergänze Infos und veröffentliche es. Wir unterstützen dich beim Start."
    },
    {
      step: "4",
      title: "Gäste gewinnen",
      description: "Gäste finden dich, sehen deine Speisekarte und kontaktieren dich direkt – per Anruf, Route oder Bestellung."
    }
  ];

  // FAQ nach Einwandlogik sortiert: Kosten → Aufwand/Setup → Aktualität → SEO/Sichtbarkeit → Design-Service
  const faqs = [
    {
      question: "Was kostet SpeiseGURU?",
      answer: "Die Basis-Registrierung ist komplett kostenlos. Du kannst dein Restaurant-Profil kostenlos erstellen, deine Speisekarte hochladen und von Gästen gefunden werden. Es gibt keine versteckten Kosten und keine monatlichen Gebühren für den Basiseintrag."
    },
    {
      question: "Wie schnell bin ich online?",
      answer: "Die Registrierung dauert weniger als 3 Minuten. Du kannst dein Profil sofort anlegen und die Speisekarte später hochladen. Sobald dein Profil veröffentlicht ist, können Gäste dich finden."
    },
    {
      question: "Woher kommen die Speisekarten?",
      answer: "Alle Speisekarten werden direkt von den Restaurants bereitgestellt. Wir sammeln keine veralteten Informationen von Drittanbietern. Du hast die volle Kontrolle über deine Speisekarte und kannst sie jederzeit aktualisieren."
    },
    {
      question: "Kann ich Öffnungszeiten und Infos selbst ändern?",
      answer: "Ja, du hast vollen Zugriff auf dein Profil. Du kannst Öffnungszeiten, Kontaktdaten, Beschreibungen und natürlich deine Speisekarte jederzeit selbstständig aktualisieren."
    },
    {
      question: "Wie funktioniert SEO und Standortoptimierung?",
      answer: "Dein Restaurant-Profil ist SEO-optimiert, damit es in Google und anderen Suchmaschinen gefunden wird. Die Standortoptimierung sorgt dafür, dass Gäste in deiner Nähe dich zuerst sehen, wenn sie nach Restaurants suchen."
    },
    {
      question: "Was ist der Design-Service genau?",
      answer: "Unser Design-Service unterstützt Restaurants bei der professionellen Gestaltung von Speisekarten, Flyern, Gutscheinen und Visitenkarten. Wir übernehmen Design und Optimierung auf Wunsch – damit du dich auf dein Restaurant konzentrieren kannst."
    },
    {
      question: "Wie kann ich mehr Features buchen?",
      answer: "Der Basiseintrag ist kostenlos und enthält alle wichtigen Features. Für erweiterte Funktionen wie Premium-Platzierung, erweiterte Analytics oder Design-Services kannst du uns gerne kontaktieren. Wir beraten dich individuell."
    }
  ];

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <SEO 
        title="Mehr Gäste gewinnen für dein Restaurant"
        description="Steigere deine Sichtbarkeit, gewinne mehr Gäste und präsentiere dein Restaurant professionell mit SpeiseGuru."
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary-dark via-primary to-primary-light text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
                            radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)`
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6 tracking-tight leading-[1.15] pb-2">
              Warum <span className="text-white/90">SpeiseGURU</span>?
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
              Gewinne mehr Gäste – mit deiner digitalen Speisekarte, besserer Sichtbarkeit und direkten Kontaktmöglichkeiten.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
              <Link href="/restaurants/create">
                <Button
                  size="lg"
                  className="bg-white text-primary hover:bg-white/95 hover:shadow-2xl rounded-full px-8 py-6 text-lg font-semibold focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary transition-colors duration-300"
                  aria-label="Jetzt kostenlos registrieren"
                >
                  Kostenlos starten
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Button>
              </Link>
              <Link href="/wir-gestalten">
                <Button
                  variant="ghost"
                  size="lg"
                  className="text-white hover:bg-white/10 hover:text-white rounded-full px-6 py-6 text-base font-medium focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary transition-colors duration-300 border border-white/20"
                  aria-label="Design-Service ansehen"
                >
                  Design-Service ansehen
                </Button>
              </Link>
            </div>

            {/* Microcopy unter Primary CTA */}
            <p className="text-sm text-white/85 mb-6 leading-relaxed">
              In 3 Minuten live – Speisekarte später hochladen. <span className="inline-flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" aria-hidden="true" /> Keine Kreditkarte</span>
            </p>

            {/* Trust Strip - harmonisiert */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-white/90">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 h-8">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                In wenigen Minuten live
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 h-8">
                <Shield className="h-3.5 w-3.5" aria-hidden="true" />
                Keine versteckten Kosten
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 h-8">
                <Smartphone className="h-3.5 w-3.5" aria-hidden="true" />
                Mobile-optimiert
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 -mt-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {[
            { number: stats.monthlyVisitors, label: "Monatliche Besucher", subline: "Aktive Nutzer", icon: <Users className="h-6 w-6" /> },
            { number: stats.restaurantsListed, label: "Restaurants gelistet", subline: "Partner-Restaurants", icon: <Utensils className="h-6 w-6" /> },
            { number: stats.menuViewsPerMonth, label: "Speisekarten-Views", subline: "Pro Monat", icon: <Eye className="h-6 w-6" /> },
            { number: stats.mobileUsage, label: "Mobile Nutzung", subline: "Der Suchenden", icon: <Smartphone className="h-6 w-6" /> }
          ].map((stat, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl p-6 shadow-lg text-center border border-gray-100 animate-fade-in-up h-full flex flex-col items-center justify-center ${index === 0 ? '' : `animation-delay-${Math.min(index * 100, 500)}`}`}
            >
              <div className="flex justify-center mb-3 text-primary" aria-hidden="true">
                {stat.icon}
              </div>
              <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
                {stat.number}
              </div>
              <div className="text-sm md:text-base text-gray-900 font-semibold mb-1 leading-tight">
                {stat.label}
              </div>
              <div className="text-xs text-gray-500 leading-relaxed">
                {stat.subline}
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-gray-500 mt-4">
          Stand: {stats.lastUpdated}
        </p>
      </section>

      {/* Benefits Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Deine Vorteile mit SpeiseGURU
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Alles, was dein Restaurant braucht, um erfolgreich zu sein
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {benefits.map((benefit, index) => (
            <article
              key={index}
              className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl hover:border-primary hover:border-2 transition-colors duration-300 h-full border border-gray-100 flex flex-col animate-fade-in-up focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-white ${index === 0 ? '' : `animation-delay-${Math.min(index * 100, 500)}`}`}
              tabIndex={0}
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${benefit.color} flex items-center justify-center text-white mb-6 shadow-lg`} aria-hidden="true">
                {benefit.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {benefit.title}
              </h3>
              <p className="text-sm text-primary font-medium mb-3">
                {benefit.description}
              </p>
              {/* Outcome-Zeile */}
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                {benefit.outcome}
              </p>
              <ul className="space-y-2.5 flex-grow">
                {benefit.bullets.map((bullet, bulletIndex) => (
                  <li key={bulletIndex} className="flex items-start text-gray-600 text-base leading-relaxed">
                    <CheckCircle2 className="h-4 w-4 text-primary mr-2.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white pt-20 pb-24 overflow-visible">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              So einfach geht's
            </h2>
            <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto leading-relaxed">
              In 4 einfachen Schritten zu mehr Gästen
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 overflow-visible">
            {process.map((item, index) => (
              <article
                key={index}
                className={`relative animate-fade-in-up overflow-visible ${index === 0 ? '' : `animation-delay-${Math.min(index * 100, 500)}`}`}
              >
                <div className="bg-gray-800/60 backdrop-blur-sm rounded-2xl p-10 pt-14 border border-gray-700 hover:border-primary hover:shadow-xl hover:shadow-primary/20 transition-colors duration-300 h-full flex flex-col min-h-[240px] overflow-visible">
                  {/* Step Badge - exakt gleich positioniert */}
                  <div className="absolute top-6 right-6 w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
                    {item.step}
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-white">
                    {item.title}
                  </h3>
                  <p className="text-gray-200 leading-relaxed text-base flex-grow">
                    {item.description}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {/* Trust Statement - als klickbarer Link */}
          <div className="mt-12 text-center">
            <Link href="/wir-gestalten">
              <p className="text-gray-300 text-base md:text-lg leading-relaxed max-w-3xl mx-auto hover:text-white transition-colors inline-flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-gray-800 rounded-md px-2 py-1">
                Wir unterstützen dich beim Start – auf Wunsch übernehmen wir Design und Optimierung.
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Features List */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-primary/10 to-primary-light/10 rounded-3xl p-12 md:p-16">
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Alles inklusive
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Diese Features sind bereits in deinem kostenlosen Profil enthalten
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {Object.entries(featuresByCategory).map(([key, category], index) => (
              <div
                key={key}
                className={`animate-fade-in-up ${index === 0 ? '' : `animation-delay-${Math.min(index * 100, 500)}`}`}
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {category.title}
                </h3>
                <ul className="space-y-3">
                  {category.items.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-start"
                    >
                      <CheckCircle2 
                        className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${
                          feature.includes("Keine versteckten Kosten") 
                            ? "text-primary" 
                            : "text-primary/70"
                        }`}
                        aria-hidden="true"
                      />
                      <span className={`text-base leading-relaxed ${
                        feature.includes("Keine versteckten Kosten")
                          ? "text-gray-900 font-semibold"
                          : "text-gray-700"
                      }`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / Mission Section */}
      <section className="bg-white border-y border-gray-200 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Warum wir das machen
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto mb-4">
            Wir wollen, dass Gäste echte Speisekarten sehen – und Restaurants weniger Zeit am Telefon verlieren. 
            Mit SpeiseGURU finden Gäste schneller das richtige Restaurant und Restaurants gewinnen gezieltere Gäste.
          </p>
          <p className="text-base text-gray-600 leading-relaxed max-w-3xl mx-auto mb-4">
            Echte Speisekarten direkt vom Restaurant bedeuten: keine veralteten Informationen, keine falschen Erwartungen, 
            keine verschwendete Zeit. Gäste können schneller entscheiden, Restaurants sparen Zeit am Telefon und beide Seiten profitieren von besseren Entscheidungen.
          </p>
          <p className="text-base text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
            Das ist unsere Mission: Restaurant-Suche einfacher, transparenter und erfolgreicher zu machen – für Gäste und Restaurants gleichermaßen.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/restaurants">
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-full px-6 py-6 text-base font-medium focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-colors duration-300"
                aria-label="Beispielprofil ansehen"
              >
                Beispielprofil ansehen
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Häufige Fragen
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Alles, was Restaurants über SpeiseGURU wissen müssen
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-3">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="bg-white rounded-lg border border-gray-200 px-6 shadow-sm hover:shadow-md hover:border-primary/30 transition-colors duration-200"
            >
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-6 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-6 text-base">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Contact Link */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-2">
            Noch Fragen?
          </p>
          <Link href="/kontakt">
            <span className="inline-flex items-center text-primary hover:text-primary-dark font-medium text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md px-2 py-1">
              Kontaktiere uns
              <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </span>
          </Link>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-gradient-to-r from-primary-dark to-primary rounded-3xl p-12 md:p-16 text-center text-white shadow-2xl animate-scale-in">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Bereit, mehr Gäste zu gewinnen?
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            Starte kostenlos – dein Profil ist in wenigen Minuten online.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <Link href="/restaurants/create">
              <Button
                size="lg"
                className="bg-white text-primary hover:bg-white/95 hover:shadow-2xl rounded-full px-8 py-6 text-lg font-semibold focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary transition-colors duration-300"
                aria-label="Kostenlos starten"
              >
                Kostenlos starten
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Button>
            </Link>
            <Link href="/wir-gestalten">
              <Button
                variant="ghost"
                size="lg"
                className="text-white hover:bg-white/10 hover:text-white rounded-full px-6 py-6 text-base font-medium focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary transition-colors duration-300 border border-white/20"
                aria-label="Design-Service ansehen"
              >
                Design-Service
              </Button>
            </Link>
          </div>
          {/* Microcopy */}
          <p className="text-sm text-white/85 leading-relaxed">
            Kostenlos • in Minuten online • jederzeit erweiterbar
          </p>
        </div>
      </section>
    </main>
  );
};

export default WarumSpeiseGuruPage;
